/**
 * /guides/how-to-organize-wedding-guest-list
 *
 * "How to Organize a Wedding Guest List" — the eighth production guide, and the
 * one the RSVP cluster was missing: the guides on either side of it assume the
 * list already exists.
 *
 * Its argument is a sequence, and every section is one layer of it:
 *
 *   guest-count ceiling -> people -> invitation groups -> individual attendees
 *   -> rules (plus-ones, children) -> invitation status -> RSVP status ->
 *   final headcount
 *
 * The list is treated throughout as the working record later decisions are made
 * from, not as a collection of names. An edit that turns any section into a
 * standalone tip has broken the sequence, which is the piece.
 *
 * ── Deliberately NOT the RSVP guide, and NOT the seating guide ────────────
 * `how-to-organize-wedding-rsvps` owns everything that happens after the
 * invitations go out: statuses, follow-ups, reconciling replies, the final
 * count. This guide stops at the point invitations are sent and hands over,
 * once, in "Invited Is Not the Same as Attending". No status model, no
 * follow-up list, no channel problem — all of that is the other article's.
 *
 * `wedding-seating-plan` owns the tables. This guide may say that a list
 * organized by household makes seating easier, and it links there from the
 * chain section. It may not explain how to seat anyone.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * A guest list is a planning problem and a personal decision, and the article
 * must never confuse the two. There is no correct guest count, no correct
 * plus-one policy and no correct children policy; every section that touches
 * one says so, and offers a common approach rather than a rule. What the guide
 * IS willing to assert is process: decide the rule before you need it, apply it
 * the same way to everyone, and keep one authoritative record.
 *
 * Banned outright: "you must invite", "you should never invite", etiquette
 * conventions stated as universal law, guilt as an argument, any assumption
 * about who a reader's family is or how it is arranged, and any formula that
 * produces a guest number from a budget. The parents section is written as a
 * division of labour, never as a confrontation, and the cutting section is a
 * rule applied evenly, never a verdict on a person.
 *
 * The A-list/B-list idea appears once, briefly, with its cost named. It is not
 * the framework the article is built on: prioritizing is, because it survives
 * the cases a two-list model cannot express.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Four, all in the closing section, and nothing beyond them:
 *
 *   - "Agapaé's guest list and RSVP tools are available on the Heirloom plan."
 *     True at `api/constants/planFeatures.mjs`: the guest list is part of
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - invited guests and the people on each invitation live in one list
 *     (GuestSchema is one invitation; GuestPartySchema holds its members)
 *   - the couple records who has been invited  (`invitationSent` on GuestSchema)
 *   - a companion a guest asks to bring arrives as a request beside the guest
 *     who asked, and counts once the couple approves it  (the already-published
 *     wording on /wedding-rsvp, and `reviewStatus` on PartyMemberSchema)
 *
 * And the things this guide must never say, because the product does not do
 * them: generate a guest list, decide who is invited, send reminders or
 * follow-ups automatically, merge duplicates on its own, or produce a seating
 * plan without the couple. Guests do not edit the couple's guest list.
 *
 * One capability is deliberately left unmentioned rather than denied: the guest
 * list has a spreadsheet import. It is outside what this article was
 * commissioned to claim, and the piece reads no differently without it.
 *
 * ── Structure ─────────────────────────────────────────────────────────────
 * Section numbers are dropped, as in every guide since the third: the headings
 * then read as an argument rather than a checklist, and the two places order
 * genuinely carries meaning — cutting the list, and the workflow — are `steps`
 * lists, which number themselves.
 *
 * Two contextual links, each at the point a reader needs it: the RSVP guide
 * where the article stops being about the list, and the seating guide where the
 * chain reaches the tables. `related.guides` names the RSVP guide as well —
 * this is the cluster's real spine, the same call the photo pair made, and two
 * links to one guide stays under the crawler-stuffing cap the test enforces.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const howToOrganizeWeddingGuestListEn = {
  slug: "how-to-organize-wedding-guest-list",

  title: "How to Organize a Wedding Guest List",
  metaTitle: "How to Organize a Wedding Guest List | Agapaé",
  metaDescription:
    "Learn how to organize a wedding guest list, set your guest count, group households, manage plus-ones and children, and keep your list ready for RSVPs.",

  eyebrow: "Wedding guest list",

  hero: {
    src: "/assets/images/invitation/invitation-3.jpg",
    // Describes the photograph, not the keyword: a black and white outdoor
    // scene, the couple walking across a field in low sun, a wooden fence
    // behind them, and — small, at the right edge — a handful of people
    // further down the path. Those distant figures are why it is the right
    // picture for a guide about guests, and they are genuinely in the frame.
    // Nothing is described here that is not.
    alt: "A black and white photograph of a bride and groom walking side by side across a sunlit field, the bride carrying a bouquet, a wooden fence behind them and a small group of people further down the path in the distance",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Almost everything else about a wedding waits on one number. The venue, the budget, the invitations, the catering and the tables all depend on how many people are coming, and on which people they are.`,
    `Which is why a list that begins as a page of names becomes a problem: names are easy to add and uncomfortable to remove.`,
    `The way through is to organize it in layers: the guest count you have agreed on, the people inside it, the households they belong to, the rules that decide who else is included, and the status each invitation carries.`,
    `Set the boundaries first, then organize the people inside them: a wedding guest list that stays useful from the first draft to the final headcount.`,
  ],

  sections: [
    {
      heading: "Start With Your Guest Count, Not the Names",
      content: [
        `Before the first name goes down, agree roughly how large the wedding can be.`,
        `That ceiling is shaped by a handful of practical things:`,
        {
          type: "list",
          items: [
            `what your venue can hold`,
            `your budget, since most wedding costs are counted per person`,
            `the atmosphere you want`,
            `constraints nobody chose, such as travel or accommodation`,
          ],
        },
        `There is no right number, and no formula that produces one. A small wedding is not a compromise and a large one is not excess.`,
        `What matters is that the limit exists before the list does. A ceiling agreed afterwards is not a ceiling; it is a negotiation.`,
      ],
    },
    {
      heading: "Keep One Master Guest List",
      content: [
        `A wedding guest list goes wrong long before anyone is cut from it: it goes wrong when there is more than one of it.`,
        `Names collect wherever they are first mentioned:`,
        {
          type: "list",
          items: [
            `notes on a phone`,
            `message threads between the two of you`,
            `a spreadsheet one of you started`,
            `a document a parent is keeping`,
          ],
        },
        `Each is right about something and wrong about the total, and the disagreement surfaces while ordering invitations, or while giving a caterer a number.`,
        `Keep one authoritative record. A spreadsheet, a shared document, a dedicated guest list organizer: the tool matters far less than the rule that there is one list, and it is right.`,
      ],
    },
    {
      heading: "Start With the People You Know You Want There",
      content: [
        `The first draft should be generous rather than final. It is easier to remove a name than to remember one you never wrote down.`,
        `Most couples work through something like:`,
        {
          type: "list",
          items: [
            `immediate family`,
            `extended family`,
            `closest friends`,
            `wider friend groups`,
            `colleagues`,
            `family friends`,
            `names your parents would like considered, where that applies`,
          ],
        },
        `Not every category belongs at every wedding, and none obliges you to anyone: some couples invite no colleagues at all, some a whole department.`,
        `This draft is not the invitation list; it is the pool the invitation list gets chosen from.`,
      ],
    },
    {
      heading: "Prioritize Instead of Deciding Every Name at Once",
      content: [
        `Most of the difficulty comes from a handful of borderline cases, and settling each on the spot is what makes the task feel impossible.`,
        `A simple priority marking moves faster:`,
        {
          type: "list",
          items: [
            `Priority 1, the people you already know you want there`,
            `Priority 2, the people you would like to invite if there is room`,
            `Priority 3, the people you would enjoy including if the numbers allow`,
          ],
        },
        `Nothing is refused at this stage. A guest at priority three is waiting on a number you do not have yet.`,
        `Some couples formalize this as an A-list and a B-list, inviting from the second as declines arrive. It can work, with care: nobody should be able to tell they were somebody else's cancellation.`,
      ],
    },
    {
      heading: "Decide Your Plus-One Policy Before You Need It",
      content: [
        `Plus-ones move a headcount more than any other decision on the list. Twenty invitations that include a companion are twenty more people at dinner.`,
        `Couples weigh a few things when deciding who is offered one:`,
        {
          type: "list",
          items: [
            `guests who are married, engaged or in a long-term relationship`,
            `members of the wedding party`,
            `guests who would otherwise know almost nobody in the room`,
          ],
        },
        `None of these is a rule: a wedding that offers a plus-one to everyone is not more generous than one that offers none, only a different size.`,
        `What matters operationally is consistency: decide the policy before invitations go out, and apply it the same way to everyone.`,
      ],
    },
    {
      heading: "Decide How You Will Handle Children",
      content: [
        `The same is true of children, and this one reaches your guests earlier: families need time to arrange the day around it.`,
        `Couples take different approaches:`,
        {
          type: "list",
          items: [
            `children generally invited`,
            `children of immediate family only`,
            `an adults-only celebration`,
            `decisions made case by case`,
          ],
        },
        `No approach is more correct than another. What people notice is inconsistency, not the policy itself.`,
        `Whichever you choose, say so on the invitation, and record per invitation whether children are included and how many — which is what your catering and your tables will need.`,
      ],
    },
    {
      heading: "Organize Guests by Household, Not Only by Name",
      content: [
        `This is the structure that makes everything afterwards easier, and the one most lists are missing.`,
        `Two different things have to be visible at once:`,
        {
          type: "list",
          items: [
            `the invitation group, meaning who receives one invitation`,
            `the individual attendees, meaning the people that invitation covers`,
          ],
        },
        `One invitation goes to Maria Papadopoulou and Nikos Papadopoulos: one invitation group, two attendees. If their two children are included, it is still one invitation and four people. An invitation to a single guest with a companion is one group, one named attendee and one unnamed place.`,
        `Every envelope and message counts groups; every headcount, catering figure and table counts people. A list that expresses only one of them has to be recounted by hand every time you need the other.`,
        `It also answers the later question of who belongs together.`,
      ],
    },
    {
      heading: "Track the Right Information, and Not More",
      content: [
        `A practical wedding guest list usually ends up holding:`,
        {
          type: "list",
          items: [
            `full name`,
            `the household or invitation group`,
            `how you know them`,
            `contact details, for however you plan to invite them`,
            `whether the invitation has been sent`,
            `whether the invitation includes a companion`,
            `children and age category, where the wedding needs it`,
            `RSVP status, once invitations are out`,
            `notes that affect planning`,
            `the table they end up at, much later`,
          ],
        },
        `Not all at once. Most are useless in month one and essential in month nine, so add each when it starts doing work.`,
        `Collect what your wedding actually uses, and stop there. All of it is other people's personal information, held on their behalf, and a field you never open is one more thing to keep current.`,
      ],
    },
    {
      heading: "Invited Is Not the Same as Attending",
      content: [
        `A guest list answers who you invited. An RSVP answers who is coming. Keeping them separate stops the list dissolving into a pile of replies.`,
        `At any moment, a guest can be:`,
        {
          type: "list",
          items: [
            `invited`,
            `invited and not yet replied`,
            `attending`,
            `declining`,
          ],
        },
        `The invitation is settled; the response is not, and can change more than once. So the list keeps the invitation context underneath the current answer: a guest who declines is still someone you invited.`,
        {
          type: "p",
          text: [
            `Once invitations are out, the work changes: it stops being about building the list and starts being about keeping the answers straight. `,
            {
              text: "How to Organize Wedding RSVPs Without Losing Track of Guests",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` picks that up from here.`,
          ],
        },
      ],
    },
    {
      heading: "When Parents Are Adding Names",
      content: [
        `Where parents are involved — contributing guests, money or both — names arrive from outside the two of you. This is ordinary, and easier handled as process than authority.`,
        `It helps to agree, early and out loud:`,
        {
          type: "list",
          items: [
            `roughly how many names they can put forward`,
            `where those names sit against your own priorities`,
            `that anything suggested still has to fit inside the total guest count`,
          ],
        },
        `The useful separation is between suggesting a name and deciding the total: suggesting is open to everyone, the total belongs to whoever is planning and paying for the day.`,
        `Suggested names belong on the same master list, marked as suggestions until they are decided.`,
      ],
    },
    {
      heading: "How to Cut Down a Wedding Guest List",
      content: [
        `Most lists overshoot at least once. Cutting one is uncomfortable, and much less so done by rule rather than by verdict.`,
        {
          type: "steps",
          items: [
            `Go back to the guest-count ceiling and confirm it is still the number.`,
            `Decide the criteria, and apply them to everyone equally.`,
            `Start with the lowest priority group rather than the whole list.`,
            `Look for overlaps: a household entered twice, a companion already listed as a guest.`,
            `Revisit the plus-one and children policies if they were never explicitly decided; an unstated one is usually the most expensive.`,
            `Make the changes before invitations are sent wherever you still can.`,
          ],
        },
        `A few questions do most of the work:`,
        {
          type: "list",
          items: [
            `When were we last meaningfully in touch?`,
            `Would we genuinely notice they were not there?`,
            `Are we inviting them because we want them, or out of obligation?`,
            `Have we applied the same reasoning to everyone in a similar position?`,
          ],
        },
        `A rule applied evenly is easier to live with than a run of individual verdicts.`,
      ],
    },
    {
      heading: "Keep the List Current as Plans Change",
      content: [
        `A guest list is not finished when the first draft is. It changes for normal reasons:`,
        {
          type: "list",
          items: [
            `new relationships`,
            `plus-one situations that change`,
            `invitation decisions you revisit`,
            `declines`,
            `late additions`,
            `cancellations`,
            `changes within families`,
          ],
        },
        `Update the same record rather than making a copy. A folder holding "guest list final" and "guest list final v2" is one where nobody can tell which is true.`,
        `The test is simple. If someone asked today how many people are coming, could you answer from the list without checking anything else?`,
      ],
    },
    {
      heading: "When Is Your Guest List Ready?",
      content: [
        `The list is ready for invitations and RSVPs when:`,
        {
          type: "list",
          items: [
            `the guest-count ceiling is agreed`,
            `the people being invited are decided`,
            `the plus-one policy is clear`,
            `the children policy is clear`,
            `households and invitation groups are visible in the list`,
            `duplicate entries have been removed`,
            `contact details are complete enough to send an invitation`,
            `there is one authoritative version of it`,
          ],
        },
        `Ready does not mean frozen: guests will still be added and removed after invitations go out, and a list that absorbs that is working as intended.`,
      ],
    },
    {
      heading: "From Guest List to RSVP to Seating",
      content: [
        `The list is the first stage of a chain, each stage adding something to the one before:`,
        {
          type: "steps",
          items: [
            `The guest list decides who is invited.`,
            `Invitations put that decision in front of people.`,
            `RSVPs record what those people answered.`,
            `The confirmed attendees become your final headcount.`,
            `Seating arranges those attendees into tables.`,
          ],
        },
        `Nothing later can recover what the guest list never held: if households were never grouped, they have to be reconstructed when the tables are drawn.`,
        {
          type: "p",
          text: [
            `That last stage is a subject of its own, and `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            ` covers it once your answers are in.`,
          ],
        },
      ],
    },
    {
      heading: "A Simple Wedding Guest List Workflow",
      content: [
        `Put together, the whole thing fits on one page:`,
        {
          type: "steps",
          items: [
            `Set the guest-count ceiling.`,
            `Brainstorm everyone you might invite.`,
            `Prioritize them.`,
            `Decide your plus-one and children policies.`,
            `Organize people into invitation groups and households.`,
            `Consolidate everything into one master list.`,
            `Add contact details and invitation information.`,
            `Send the invitations and begin tracking RSVPs.`,
            `Keep the same list current through to the final headcount.`,
          ],
        },
        `The order is doing real work: steps one to four are decisions, five to nine are record-keeping. Reversed, the record-keeping is redone every time a decision changes.`,
      ],
    },
    {
      // The brief closes with a separate Agapaé section, which is what the
      // `related` block below already renders as an H2. Two H2s reading the
      // same words is a defect, and every earlier guide resolved it the same
      // way: the closing prose section is named for what it does, and "Where
      // this fits in Agapaé" stays with the related block.
      heading: "A List You Can Plan From",
      content: [
        `The best wedding guest list is not the most detailed. It is the one that answers, without opening anything else:`,
        {
          type: "list",
          items: [
            `who we are inviting`,
            `who has actually been invited`,
            `who belongs together`,
            `how many people that is`,
            `what is still undecided`,
          ],
        },
        `A guest list is a planning document and a set of personal decisions at the same time. The decisions are yours; the document only has to keep them straight.`,
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type every earlier guide put theirs.
          type: "note",
          text: `Agapaé's guest list and RSVP tools are available on the Heirloom plan.`,
        },
        `In Agapaé, the guest list is the invitation list: each invitation carries the people it covers, the couple records who has been invited, and answers arrive against the guest they came from. A companion a guest asks to bring appears as a request beside them, and counts once you approve it.`,
        {
          type: "p",
          text: [
            `For the product-specific guest list and RSVP experience, see `,
            { text: "Wedding RSVP", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the seven earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `When should I start making my wedding guest list?`,
      a: `Early, and usually before the venue is booked, because the guest count and the venue decide each other. A deliberately rough first draft is enough at that stage: you need a realistic size, not a final list. Most couples firm it up once the date and venue are settled, and finish it in time to order and address invitations.`,
    },
    {
      q: `How many people should I invite to my wedding?`,
      a: `There is no correct number and no formula that produces one. The useful version of the question is how many you can invite, which comes from your venue's capacity, your budget on a per-person basis, and the kind of day you want. Agree that ceiling before you build the list, so the list is written into a known space.`,
    },
    {
      q: `How should I organize a wedding guest list?`,
      a: `In layers rather than as one flat list of names: the guest count first, then the people, then the households each invitation covers, then your plus-one and children policies, then invitation and RSVP status. Keep it in one authoritative record, whatever the tool, so there is never a second version disagreeing with it.`,
    },
    {
      q: `Who should get a plus-one at a wedding?`,
      a: `That is your decision, not a rule. Many couples extend a plus-one to guests who are married, engaged or in a long-term relationship, to the wedding party, and to guests who would otherwise know nobody. What matters most is consistency: decide before the invitations go out and apply the same policy to everyone in a similar position.`,
    },
    {
      q: `What information should I include on a wedding guest list?`,
      a: `Enough to make decisions and send an invitation: name, household or invitation group, how you know them, contact details, whether the invitation has been sent, whether it includes a companion, children where relevant, and RSVP status once replies begin. Add fields when they start being useful rather than all at once.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The next stage of the chain, and the guide this article deliberately
    // stops short of. The prose links it once already, at the point the subject
    // changes; naming it here as well is the cluster relationship rather than
    // link volume, and stays under the cap the test enforces. A slug that is
    // not registered fails the test rather than shipping a 404.
    guides: ["how-to-organize-wedding-rsvps"],

    product: [
      {
        href: "/wedding-rsvp",
        label: "Wedding RSVP",
        blurb:
          "See how your guest list and RSVPs work in Agapaé, from the invitations you send and the people each one covers to the answers you review before the rest of your planning. Available on the Heirloom plan.",
      },
    ],
  },
};

export default howToOrganizeWeddingGuestListEn;
